import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "../../lib/utils";
import {
  Children,
  forwardRef,
  isValidElement,
  type ReactNode,
} from "react"; // Adjusted path based on typical project structure

/**
 * Walk the children tree and return true if any descendant element's type is
 * `DialogDescription` (or the underlying Radix Description primitive). Used
 * by DialogContent to decide whether to auto-emit a hidden placeholder
 * description — silencing Radix' `Missing Description for DialogContent`
 * warning without forcing every modal author to remember the boilerplate.
 */
function containsDialogDescription(node: ReactNode): boolean {
  let found = false;
  Children.forEach(node, (child) => {
    if (found || !isValidElement(child)) return;
    const type = child.type as unknown;
    if (
      type === DialogDescription ||
      type === DialogPrimitive.Description
    ) {
      found = true;
      return;
    }
    const props = child.props as { children?: ReactNode } | null;
    if (props && props.children !== undefined) {
      if (containsDialogDescription(props.children)) found = true;
    }
  });
  return found;
}

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * Shared Dialog content wrapper used by every modal in the dashboard
 * (FilterModal, ErrorBucketModal, FeedbackTraceDetailDialog, UserDetail…).
 *
 * A11y auto-fix: Radix' Dialog primitive sets `aria-describedby` on the
 * content element by default, which trips a console warning if no
 * `<DialogDescription>` is present in the subtree. Rather than ask every
 * caller to remember the boilerplate, we walk `children` and — if no
 * Description is found — emit a visually-hidden one with a generic
 * fallback text. The description is still announced by screen readers,
 * so the accessible contract is actually satisfied (not just muted).
 * Consumers that want a specific description still render their own
 * `<DialogDescription>` and the auto-emit is skipped.
 */
const DialogContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  const hasDescription = containsDialogDescription(children);
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-slate-200 bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg dark:border-slate-800 dark:bg-slate-950",
          className,
        )}
        {...props}
      >
        {children}
        {!hasDescription && (
          <DialogPrimitive.Description className="sr-only">
            Dialog content.
          </DialogPrimitive.Description>
        )}
        {/*
          Briefs / Quality / Feedback audit — close button hit-target was
          16×16 (the X glyph itself). Wrapper is now 44×44 (`h-11 w-11`) with
          the glyph centered, so touch users on 375px viewports can tap
          reliably without zooming. Anchored at `right-2 top-2` so the larger
          box still sits flush in the modal corner instead of stealing from
          the title row.
        */}
        <DialogPrimitive.Close className="absolute right-2 top-2 inline-flex h-11 w-11 items-center justify-center rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-slate-100 data-[state=open]:text-slate-500 dark:ring-offset-slate-950 dark:focus:ring-slate-300 dark:data-[state=open]:bg-slate-800 dark:data-[state=open]:text-slate-400">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-slate-500 dark:text-slate-400", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose, // Export DialogClose as well
  DialogPortal, // Export DialogPortal
  DialogOverlay, // Export DialogOverlay
};
